import { AttachmentListItem } from "components/AttachmentListItem";
import { AttachmentLightbox } from "components/AttachmentLightbox/AttachmentLightbox";
import { useContext, useState } from "react";
import { styled } from "@mui/material/styles";
import List from "@mui/material/List";
import { useGetAttachmentURI } from "hooks/case-action/useGetAttachmentURI";
import { downloadAsDataUri } from "core/utils/aws/downloadAsDataUri";
import { AttachmentDetail } from "interfaces/attachment";
import { useUnmount } from "react-use";
import { downloadFile } from "core/utils/aws/downloadFile";
import { QueryObserverResult } from "@tanstack/react-query";
import { getAttachment } from "core/utils/aws/getAttachment";
import { AxiosContext } from "contexts/AxiosContext";

export interface AttachmentListProps {
  /** the list of attachments to display */
  attachments?: AttachmentDetail[];
  refetchFn?: () => Promise<QueryObserverResult<any[], unknown>>;
}

/**
 * Takes a list of attachments and displays them
 * @param attachments the list of attachments to display
 */
export const AttachmentList = ({
  attachments,
  refetchFn
}: AttachmentListProps): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const [expandedImageUrl, setExpandedImageUrl] = useState<string | null>(null);
  const attachmentUri = useGetAttachmentURI(expandedImageUrl);
  const controller = new AbortController();
  useUnmount(() => {
    controller.abort();
  });

  return (
    <>
      <StyledFileList role="list" aria-label="Action Attachment List">
        {attachments?.map(attachment => (
          <AttachmentListItem
            key={attachment.id}
            filename={attachment.original_file.name}
            onExpandImage={async () => {
              try {
                await refetchFn?.();

                const refetchedData = await getAttachment(axios, attachment.id);

                let {
                  url,
                  download_headers: headers
                } = refetchedData.original_file;

                if (refetchedData.thumbnail_large) {
                  url = refetchedData.thumbnail_large.url;
                  headers = refetchedData.thumbnail_large!.download_headers;
                }

                setExpandedImageUrl(
                  await downloadAsDataUri({
                    url,
                    headers,
                    signal: controller.signal
                  })
                );
              } catch (err) {
                console.info(err);
              }
            }}
            onDelete={undefined}
            onDownloadFile={async () => {
              try {
                await refetchFn?.();

                const {
                  url,
                  download_headers: headers,
                  name: filename
                } = attachment.original_file;

                await downloadFile({
                  url,
                  headers,
                  filename,
                  signal: controller.signal
                });
              } catch (err) {
                console.info(err);
              }
            }}
          />
        ))}
      </StyledFileList>
      {attachmentUri && (
        <AttachmentLightbox
          attachmentUrl={attachmentUri}
          onClose={() => setExpandedImageUrl(null)}
        />
      )}
    </>
  );
};

const StyledFileList = styled(List)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: 0;
`;
